import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { classEnrollmentsApi } from "./client";

export function useClassEnrollmentsQuery(query: {
  limit: number;
  offset: number;
  status?: string;
  phone?: string;
}) {
  return useQuery({
    queryKey: ["class-enrollments", query],
    queryFn: () => classEnrollmentsApi.list(query),
  });
}

export function useUnconfirmedClassEnrollmentCountQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["class-enrollments", "unconfirmed-count"],
    queryFn: () => classEnrollmentsApi.unconfirmedCount(),
    enabled: options?.enabled ?? true,
    refetchInterval: 60_000,
  });
}

export function useClassEnrollmentQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["class-enrollment", id],
    queryFn: () => classEnrollmentsApi.get(id!),
    enabled: !!id,
  });
}

export function useClassEnrollmentSimilarStudentsQuery(
  id: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["class-enrollment-similar-students", id],
    queryFn: () => classEnrollmentsApi.similarStudents(id!),
    enabled: !!id && enabled,
  });
}

export function useUpdateClassEnrollmentStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { status: string; adminNotes?: string | null };
    }) => classEnrollmentsApi.updateStatus(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["class-enrollments"] });
      qc.invalidateQueries({ queryKey: ["class-enrollment", id] });
    },
  });
}

export function useConvertClassEnrollmentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { linkExistingStudentId?: string; forceCreateNew?: boolean };
    }) => classEnrollmentsApi.convert(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["class-enrollments"] });
      qc.invalidateQueries({ queryKey: ["class-enrollment", id] });
      qc.invalidateQueries({ queryKey: ["class-enrollment-similar-students", id] });
    },
  });
}
